import {
  apiV2EnergyResilienceConfigBatchDeletePost,
  apiV2EnergyResilienceConfigGasAreaAddPost,
  apiV2EnergyResilienceConfigGasAreaUpdatePost,
  apiV2EnergyResilienceConfigGasCompressorAddPost,
  apiV2EnergyResilienceConfigGasCompressorUpdatePost,
  apiV2EnergyResilienceConfigGasOutletAddPost,
  apiV2EnergyResilienceConfigGasOutletUpdatePost,
  apiV2EnergyResilienceConfigGasPlantUpdatePost,
  apiV2EnergyResilienceConfigGasStationAddPost,
  apiV2EnergyResilienceConfigGasStationUpdatePost,
  apiV2EnergyResilienceConfigLineAddPost,
  apiV2EnergyResilienceConfigLineUpdatePost,
} from '@maxtropy/device-customer-apis-v2';
import { ConfigDimension, ConfigMediumType, MediumType, ScopeKey } from '../types';
import { SaveConfigParams } from './types';

interface SelectValue {
  label?: unknown;
  value?: unknown;
}

const getSelectValue = (value: unknown) =>
  value && typeof value === 'object' && 'value' in value ? (value as SelectValue).value : value;

const getSelectLabel = (value: unknown) =>
  value && typeof value === 'object' && 'label' in value ? (value as SelectValue).label : getSelectValue(value);

const getSelectValues = (value: unknown): number[] =>
  (Array.isArray(value) ? value : []).map(getSelectValue).map(Number).filter(Number.isFinite);

export const deleteConfigs = (params: {
  currentDimension: ConfigDimension;
  currentMediumType?: ConfigMediumType;
  ids: Array<string | number>;
}) =>
  apiV2EnergyResilienceConfigBatchDeletePost({
    mediumType: params.currentMediumType,
    dimension: params.currentDimension,
    ids: params.ids.map(Number),
  });

export const saveConfig = ({
  activeEnergy,
  currentMediumType,
  currentScope,
  editingRecord,
  optionsByField,
  values,
}: SaveConfigParams) => {
  const ratedFlow = Number(values.ratedFlow);
  const flowMeterDeviceIds = values.flowMeterDeviceIds as number[];
  const gasSources = (values.gasSources as any)?.map((item: any) => ({ id: item.value, name: item.label }));
  const circuitKey = getSelectValue(values.circuitKey);
  const stationName = getSelectLabel(values.stationName) as string;
  const areaName = getSelectLabel(values.areaName) as string;
  const compressorName = getSelectLabel(values.compressorName) as string;
  const compressor = optionsByField.compressorName?.find(item => item.value === getSelectValue(values.compressorName));
  const areaIds = getSelectValues(values.areaIds);

  if (activeEnergy === MediumType.Electricity) {
    const circuit = optionsByField.circuitKey?.find(item => item.value === circuitKey);
    return editingRecord
      ? apiV2EnergyResilienceConfigLineUpdatePost({
          id: editingRecord.id,
          circuitStart: values.circuitStart as string,
          circuitEnd: values.circuitEnd as string,
          ratedCurrent: Number(values.ratedCurrent),
          areaIds,
        })
      : apiV2EnergyResilienceConfigLineAddPost({
          circuitName: circuit?.circuitName as string,
          circuitCode: circuit?.circuitCode as string,
          circuitStart: values.circuitStart as string,
          circuitEnd: values.circuitEnd as string,
          ratedCurrent: Number(values.ratedCurrent),
          areaIds,
        });
  }

  if (currentScope === ScopeKey.Factory) {
    return apiV2EnergyResilienceConfigGasPlantUpdatePost({
      id: editingRecord?.id || undefined,
      mediumType: currentMediumType,
      ratedFlowRate: ratedFlow,
      flowMeterDeviceIds,
    });
  }

  if (currentScope === ScopeKey.Station) {
    return editingRecord
      ? apiV2EnergyResilienceConfigGasStationUpdatePost({
          id: editingRecord.id,
          ratedFlowSpeed: ratedFlow,
          flowMeterDeviceIds,
        })
      : apiV2EnergyResilienceConfigGasStationAddPost({
          mediumType: currentMediumType,
          stationName,
          ratedFlowSpeed: ratedFlow,
          flowMeterDeviceIds,
        });
  }

  if (currentScope === ScopeKey.Area) {
    return editingRecord
      ? apiV2EnergyResilienceConfigGasAreaUpdatePost({
          id: editingRecord.id,
          gasSources,
          ratedFlowRate: ratedFlow,
          flowMeterDeviceIds,
        })
      : apiV2EnergyResilienceConfigGasAreaAddPost({
          mediumType: currentMediumType,
          areaName,
          gasSources,
          ratedFlowRate: ratedFlow,
          flowMeterDeviceIds,
        });
  }

  if (currentScope === ScopeKey.Outlet) {
    return editingRecord
      ? apiV2EnergyResilienceConfigGasOutletUpdatePost({
          id: editingRecord.id,
          gasSources,
          ratedFlowRate: ratedFlow,
          flowMeterDeviceIds,
        })
      : apiV2EnergyResilienceConfigGasOutletAddPost({
          mediumType: currentMediumType,
          outletName: values.outletName as string,
          gasSources,
          ratedFlowRate: ratedFlow,
          flowMeterDeviceIds,
        });
  }

  return editingRecord
    ? apiV2EnergyResilienceConfigGasCompressorUpdatePost({
        id: editingRecord.id,
        ratedFlowSpeed: ratedFlow,
        flowMeterDeviceIds,
      })
    : apiV2EnergyResilienceConfigGasCompressorAddPost({
        compressorDeviceId: compressor?.deviceId as number | undefined,
        compressorName: (compressor?.deviceName ?? compressorName) as string,
        ratedFlowSpeed: ratedFlow,
        flowMeterDeviceIds,
      });
};
